import { Outlet, createRootRoute, HeadContent, Scripts, ClientOnly, ScriptOnce } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { ensureUserCoords } from "@/lib/user-location";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { AuthProvider } from "@/hooks/useAuth";
import { I18nProvider } from "@/i18n/I18nProvider";
import { Toaster } from "@/components/ui/sonner";
import { ElevenLabsWidget } from "@/components/ai/ElevenLabsWidget";
import { PageViewTracker } from "@/components/analytics/PageViewTracker";
import { MfaEnforcer } from "@/components/MfaEnforcer";
import { themeBootScript } from "@/components/ThemeToggle";



import appCss from "../styles.css?url";

const SITE = "https://heallexa.com";

export const Route = createRootRoute({
  head: () => ({
    meta: [
      { charSet: "utf-8" },
      { name: "viewport", content: "width=device-width, initial-scale=1, viewport-fit=cover" },
      { name: "theme-color", content: "#3b82f6" },
      { name: "apple-mobile-web-app-capable", content: "yes" },
      { name: "apple-mobile-web-app-status-bar-style", content: "default" },
      { name: "apple-mobile-web-app-title", content: "Heallexa" },
      { name: "mobile-web-app-capable", content: "yes" },
      { name: "google-site-verification", content: "E2k1sNeVHuWAQJJosYIytOoyFmn6VlvHt5LcfjhIRJ8" },
      { name: "msvalidate.01", content: "450b787a709b4cc694741ace354a32d2" },
      { name: "facebook-domain-verification", content: "6yps3r001rb7gxolud9uym1sshnqbu" },
      { title: "Heallexa | Find a Doctor, Compare Providers & Book Online" },
      { name: "description", content: "The free alternative to Zocdoc. Find doctors, dentists, and clinics by specialty, insurance, and city. AI-powered. Zero booking fees." },
      { name: "author", content: "Heallexa" },
      { property: "og:site_name", content: "Heallexa" },
      { property: "og:title", content: "Heallexa | Find a Doctor, Compare Providers & Book Online" },
      { property: "og:description", content: "The free alternative to Zocdoc. Find doctors, dentists, and clinics by specialty, insurance, and city. AI-powered. Zero booking fees." },
      { property: "og:type", content: "website" },
      { property: "og:locale", content: "en_US" },
      { property: "og:image", content: `${SITE}/og-image.jpg` },
      { property: "og:image:width", content: "1200" },
      { property: "og:image:height", content: "630" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:site", content: "@heallexahq" },
      { name: "twitter:creator", content: "@obristbuasa_" },
      { name: "twitter:title", content: "Heallexa | Find a Doctor, Compare Providers & Book Online" },
      { name: "twitter:description", content: "The free alternative to Zocdoc. Find doctors, dentists, and clinics by specialty, insurance, and city. AI-powered. Zero booking fees." },
      { name: "twitter:image", content: `${SITE}/og-image.jpg` },
    ],
    links: [
      { rel: "stylesheet", href: appCss },
      { rel: "icon", href: "/favicon.ico", sizes: "any" },
      { rel: "icon", type: "image/png", sizes: "16x16", href: "/favicon-16.png" },
      { rel: "icon", type: "image/png", sizes: "32x32", href: "/favicon-32.png" },
      { rel: "icon", type: "image/png", sizes: "512x512", href: "/favicon-512.png" },
      { rel: "apple-touch-icon", sizes: "180x180", href: "/apple-touch-icon.png" },
      { rel: "manifest", href: "/manifest.webmanifest" },
      // Google Fonts — preconnect to eliminate DNS + TLS overhead on first render
      { rel: "preconnect", href: "https://fonts.googleapis.com" },
      { rel: "preconnect", href: "https://fonts.gstatic.com", crossOrigin: "anonymous" },
      { rel: "stylesheet", href: "https://fonts.googleapis.com/css2?family=DM+Sans:ital,opsz,wght@0,9..40,100..1000;1,9..40,100..1000&family=Plus+Jakarta+Sans:ital,wght@0,200..800;1,200..800&display=swap" },
      // ElevenLabs widget CDN — preconnect so the widget loads faster
      // (unpkg.com serves the @elevenlabs/convai-widget-embed package)
      { rel: "preconnect", href: "https://unpkg.com" },
      { rel: "dns-prefetch", href: "https://unpkg.com" },
    ],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "WebSite",
          "@id": `${SITE}/#website`,
          name: "Heallexa",
          alternateName: "Heallexa Healthcare Finder",
          url: SITE,
          description:
            "The free alternative to Zocdoc. Find doctors, dentists, and clinics by specialty, insurance, and city. AI-powered. Zero booking fees.",
          potentialAction: {
            "@type": "SearchAction",
            target: { "@type": "EntryPoint", urlTemplate: `${SITE}/search?q={search_term_string}` },
            "query-input": "required name=search_term_string",
          },
          publisher: { "@id": `${SITE}/#organization` },
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Organization",
          "@id": `${SITE}/#organization`,
          name: "Heallexa",
          legalName: "Heallexa Inc.",
          alternateName: ["Heallexa Healthcare Search", "Heallexa Inc"],
          url: SITE,
          logo: {
            "@type": "ImageObject",
            "@id": `${SITE}/#logo`,
            url: `${SITE}/logo.png`,
            contentUrl: `${SITE}/logo.png`,
            width: 512,
            height: 512,
            caption: "Heallexa logo",
          },
          description:
            "Heallexa is an AI powered healthcare search platform that helps patients find healthcare providers by specialty, location, insurance, and availability.",
          foundingDate: "2026",
          foundingLocation: {
            "@type": "Place",
            name: "Delaware, United States",
          },
          address: {
            "@type": "PostalAddress",
            streetAddress: "2351 W Northwest Hwy Suite 1234",
            addressLocality: "Dallas",
            addressRegion: "TX",
            postalCode: "75220",
            addressCountry: "US",
          },
          email: "contact@heallexa.com",
          telephone: "+1-470-800-0081",
          contactPoint: {
            "@type": "ContactPoint",
            telephone: "+1-470-800-0081",
            email: "contact@heallexa.com",
            contactType: "customer service",
            areaServed: "US",
            availableLanguage: ["English", "Spanish"],
          },
          areaServed: {
            "@type": "Country",
            name: "United States",
          },
          knowsAbout: [
            "healthcare provider search",
            "doctor directory",
            "medical specialists",
            "health insurance acceptance",
            "appointment booking",
            "telemedicine",
            "patient care navigation",
          ],
          hasOfferCatalog: {
            "@type": "OfferCatalog",
            name: "Provider Listing Plans",
            itemListElement: [
              {
                "@type": "Offer",
                name: "Free Listing",
                description: "Basic practice listing with name, phone, address, and website.",
                price: "0",
                priceCurrency: "USD",
              },
              {
                "@type": "Offer",
                name: "Premium Listing",
                description: "Full profile with verified badge, photos, services, insurance, and online booking.",
                price: "29",
                priceCurrency: "USD",
                billingIncrement: "P1M",
              },
            ],
          },
          founder: {
            "@type": "Person",
            name: "Obrist Buasa",
            jobTitle: "Founder & CEO",
            url: "https://www.linkedin.com/in/obristbuasa/",
            image: `${SITE}/founder-obrist-buasa.jpg`,
            sameAs: ["https://www.linkedin.com/in/obristbuasa/"],
            description:
              "Obrist Buasa is the Founder of Heallexa, an AI powered healthcare search platform created to make it easier for patients to find healthcare providers by specialty, location, insurance, and availability.",
          },
          sameAs: [
            "https://www.facebook.com/heallexa",
            "https://www.instagram.com/heallexa",
            "https://x.com/heallexahq",
            "https://www.linkedin.com/company/heallexa",
          ],
        }),
      },
    ],
  }),
  shellComponent: RootShell,
  component: RootComponent,
});

function RootShell({ children }: { children: React.ReactNode }) {
  return (
    // lang="en-US" — explicit locale needed for accessibility and search engine
    // locale signal. "en" alone is ambiguous; "en-US" scopes to US English and
    // aligns with og:locale en_US and the US-only content strategy.
    <html lang="en-US" suppressHydrationWarning>
      <head>
        <script
          dangerouslySetInnerHTML={{
            __html: `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src='https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);})(window,document,'script','dataLayer','GTM-K88627LV');`,
          }}
        />
        <script async src="https://www.googletagmanager.com/gtag/js?id=G-3T978V6611" />
        <script
          dangerouslySetInnerHTML={{
            __html: `window.dataLayer = window.dataLayer || [];function gtag(){dataLayer.push(arguments);}gtag('js', new Date());gtag('config', 'G-3T978V6611');`,
          }}
        />
        <script
          dangerouslySetInnerHTML={{
            __html: `(function(c,l,a,r,i,t,y){c[a]=c[a]||function(){(c[a].q=c[a].q||[]).push(arguments)};t=l.createElement(r);t.async=1;t.src="https://www.clarity.ms/tag/"+i+"?ref=bwt";y=l.getElementsByTagName(r)[0];y.parentNode.insertBefore(t,y);})(window,document,"clarity","script","wxc8v7ulla");`,
          }}
        />
        <HeadContent />
      </head>
      <body>
        <noscript>
          <iframe
            src="https://www.googletagmanager.com/ns.html?id=GTM-K88627LV"
            height={0}
            width={0}
            style={{ display: "none", visibility: "hidden" }}
          />
        </noscript>
        <ScriptOnce children={themeBootScript} />
        {children}

        <Scripts />
        <script
          dangerouslySetInnerHTML={{
            __html: `(() => {
  if (!("serviceWorker" in navigator)) return;
  // Always unregister any existing service worker and clear caches.
  // A stale SW was intercepting fetches and breaking Supabase auth.
  navigator.serviceWorker.getRegistrations?.().then((rs) => rs.forEach((r) => r.unregister())).catch(() => {});
  try { caches?.keys?.().then((keys) => keys.forEach((k) => caches.delete(k))); } catch {}
})();`,
          }}
        />

      </body>
    </html>
  );
}

function RootComponent() {
  const [queryClient] = useState(() => new QueryClient());
  // Ask for the visitor's location once on site open (any entry page), so
  // provider listings can sort/label by distance. useEffect is client-only.
  useEffect(() => { ensureUserCoords(); }, []);
  return (
    <QueryClientProvider client={queryClient}>
      <I18nProvider>
        <AuthProvider>
          <ClientOnly fallback={null}>
            <MfaEnforcer />
          </ClientOnly>
          <Outlet />
          <ClientOnly fallback={null}>
            <ElevenLabsWidget />
            <PageViewTracker />
          </ClientOnly>
          <Toaster richColors position="top-right" />
        </AuthProvider>
      </I18nProvider>
    </QueryClientProvider>
  );
}
